import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePersonnelProjects } from "@/hooks/usePersonnelProjects";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Users, ChevronRight } from "lucide-react";
import { NewPersonnelProjectWizard } from "@/components/controller/personnel/NewPersonnelProjectWizard";
import { RequirePermission } from "@/components/RequirePermission";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const statusColor: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  on_hold: "bg-amber-500/10 text-amber-700 border-amber-200",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

function PersonnelProjectsInner() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { data: projects = [], isLoading } = usePersonnelProjects();
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Personnel Projects</h1>
        <Button onClick={() => setWizardOpen(true)} size={isMobile ? "sm" : "default"}>
          <Plus className="mr-2 h-4 w-4" /> {isMobile ? "New" : "New Personnel Project"}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-center py-8 text-muted-foreground">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">
          No personnel projects yet. Click "+ New Personnel Project" to get started.
        </p>
      ) : isMobile ? (
        <div className="space-y-3">
          {projects.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer active:scale-[0.98] transition-transform"
              onClick={() => navigate(`/controller/personnel/${p.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{p.project_number}</span>
                      <Badge variant="outline" className={`text-[10px] ${statusColor[p.status] ?? ""}`}>
                        {p.status.replace("_", " ")}
                      </Badge>
                    </div>
                    <p className="font-medium mt-1 truncate">{p.name}</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {p.customers?.name ?? "—"}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-lg font-bold">{p.nte_amount ? fmt(p.nte_amount) : "Open"}</p>
                    <p className="text-xs text-muted-foreground">
                      {[p.location_city, p.location_state].filter(Boolean).join(", ") || "—"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">PP#</TableHead>
                  <TableHead>Project Name</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">NTE</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow
                    key={p.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/controller/personnel/${p.id}`)}
                  >
                    <TableCell className="font-mono text-sm">{p.project_number}</TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell>{p.customers?.name ?? "—"}</TableCell>
                    <TableCell>
                      {[p.location_city, p.location_state].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColor[p.status] ?? ""}>
                        {p.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.nte_amount ? fmt(p.nte_amount) : "Open"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <NewPersonnelProjectWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}

export default function PersonnelProjects() {
  return (
    <RequirePermission anyOf={["view_projects"]}>
      <PersonnelProjectsInner />
    </RequirePermission>
  );
}